import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { useAuth } from "../../context/useAuth";
// import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { useNavigate } from "react-router-dom";
import loader from "../../images/loader.gif";

export default forwardRef(function Table(
  {
    data,
    fetchFn,
    config,
    filter,
    loading,
    emptyMessage = "No items to show",
    paginationCountText = "Elements: ",
    className,
    enableColumnVisibility = true,
    enableColumnOrdering = true,
    defaultVisibility,
    onVisibilityChange,
    rowClassName,
    rowIcon,
    hideIcon,
    defaultSort,
    sortDirection: controlledSortDirection,
    onSortChange,
    showFooter = false,
    onRowOrderChange,
    draggableRows = false,
  },
  ref
) {
  const [sortKey, setSortKey] = useState(defaultSort);
  const [sortDirection, setSortDirection] = useState(controlledSortDirection || "desc");
  const [customSortFunction] = useState(null);
  const [dragStartIndex, setDragStartIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  // const [scroll, setScroll] = useState();
  const [list, setList] = useState(data || []);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const buttonClickedRef = useRef(false);
  const popoverButtonRef = useRef(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const auth = useAuth();
  const pageSize = 40;
  const navigate = useNavigate();
  const [draggedRowIndex, setDraggedRowIndex] = useState(null);

  // useBottomScrollListener(() => {
  //   if (window.pageYOffset !== scroll) {
  //     setScroll(window.pageYOffset);
  //     if (!data) loadMore();
  //   }
  // });

  const [columnVisibility, setColumnVisibility] = useState(() => {
    if (defaultVisibility) {
      return defaultVisibility;
    } else {
      const initial = {};
      config.columns.forEach((col, index) => {
        initial[col.key] = { position: index, visibility: true };
      });
      return initial;
    }
  });

  useEffect(() => {
    if (!defaultVisibility) {
      const initial = {};
      config.columns.forEach((col, index) => {
        initial[col.key] = { position: index, visibility: true };
      });
      setColumnVisibility(initial);
    }
  }, [config.columns, defaultVisibility]);

  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef(null);

  // Close popover on click outside
  useEffect(() => {
    if (!showPopover) return;
    function handleClick(e) {
      if (buttonClickedRef.current) {
        buttonClickedRef.current = false;
        return;
      }
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setShowPopover(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showPopover]);

  const toggleVisibility = (key) => {
    const visibleColumnsCount = Object.values(columnVisibility).filter((col) => col.visibility).length;

    if (visibleColumnsCount <= 1 && columnVisibility[key].visibility) {
      return;
    }

    const updated = {
      ...columnVisibility,
      [key]: {
        ...columnVisibility[key],
        visibility: !columnVisibility[key]?.visibility,
      },
    };

    setColumnVisibility(updated);
    if (onVisibilityChange) {
      onVisibilityChange(updated);
    }
  };

  const fetchData = async (currentPage) => {
    let result = await fetchFn({ pageSize, currentPage, sortKey, sortDirection, filter });

    if (result.status !== 200) return;

    setList((list) => list.concat(result.data));

    if (result.data.length === pageSize) {
      setShowLoadMore(true);
    } else {
      setShowLoadMore(false);
    }
  };

  const loadMore = () => {
    setCurrentPage(currentPage + 1);
    fetchData(currentPage + 1);
  };

  useEffect(() => {
    if (data) {
      setList(data);
    } else {
      setList([]);
    }

    setCurrentPage(0);
    if (auth.user && fetchFn) fetchData(0, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey, sortDirection, filter, auth.user, data]);

  const capitalize = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  function getNestedValue(obj, path) {
    return path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
  }

  let filteredData = list.filter((item, key) => {
    item.index = key;
    if (fetchFn) return true;
    for (let filterKey in filter) {
      if (filter[filterKey] === "true" || filter[filterKey] === "false") {
        if (!item[filterKey]) item[filterKey] = false;
        if (filter[filterKey] && item[filterKey].toString() !== filter[filterKey].toString()) {
          return false;
        }
      } else {
        if (!filter[filterKey] && filter[filterKey] !== 0) continue;
        const value = filterKey.includes(".") ? getNestedValue(item, filterKey) : item[filterKey];
        const filterValue = filter[filterKey];

        // null/undefined
        if (value === undefined || value === null) return false;

        // String
        if (typeof value === "string") {
          if (!value.toLowerCase().includes(String(filterValue).toLowerCase())) {
            return false;
          }
        }
        // Number or boolean
        else if (typeof value === "number" || typeof value === "boolean") {
          if (value !== filterValue) {
            return false;
          }
        }
        // Fallback
        else if (value !== filterValue) {
          return false;
        }
      }
    }
    return true;
  });

  useImperativeHandle(ref, () => {
    return {
      filteredData,
      columnVisibility,
      setColumnVisibility,
    };
  }, [filteredData, columnVisibility]);

  useEffect(() => {
    if (defaultSort !== undefined && defaultSort !== sortKey) setSortKey(defaultSort);
    // eslint-disable-next-line
  }, [defaultSort]);

  useEffect(() => {
    if (controlledSortDirection && controlledSortDirection !== sortDirection) setSortDirection(controlledSortDirection);
    // eslint-disable-next-line
  }, [controlledSortDirection]);

  // Update sort and notify parent
  const setSortKeyFn = (key, sortOverride) => {
    let direction = sortDirection;
    if (sortKey === key) {
      direction = sortDirection === "asc" ? "desc" : "asc";
    } else {
      direction = sortOverride || "asc";
    }
    setSortKey(key);
    setSortDirection(direction);
    if (onSortChange) onSortChange(key, direction);
  };

  const sortFn = (a, b) => {
    if (!sortDirection || !sortKey) return 0;
    let aVal, bVal;

    if (customSortFunction) {
      aVal = customSortFunction(a);
      bVal = customSortFunction(b);
    } else {
      aVal = getAttr(a, sortKey);
      bVal = getAttr(b, sortKey);
    }

    if (!aVal && !bVal) {
      return 0;
    }
    if (!aVal) {
      return 1;
    }
    if (!bVal) {
      return -1;
    }
    if (typeof aVal === "string" && typeof bVal === "string") {
      return sortDirection === "desc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }

    if (aVal < bVal) return sortDirection === "asc" ? 1 : -1;
    if (aVal > bVal) return sortDirection === "asc" ? -1 : 1;
    return 0;
  };

  const getAttr = (obj, key) => {
    let keys = key.split(".");
    let val = obj;
    for (let k of keys) {
      if (val) val = val[k];
      if (val === undefined) return "";
    }

    return val;
  };

  const sortedColumns = config.columns
    .map((col, index) => ({
      ...col,
      position: columnVisibility[col.key]?.position !== undefined ? columnVisibility[col.key].position : index,
    }))
    .sort((a, b) => a.position - b.position);

  const visibleColumns = sortedColumns.filter((col) => columnVisibility[col.key]?.visibility);

  const handleDragStart = (e, index) => {
    if (!enableColumnOrdering) return;
    setDragStartIndex(index);
    e.dataTransfer.setData("text/plain", index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    if (!enableColumnOrdering) return;
    e.preventDefault();
    setDragOverIndex(index);
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    if (!enableColumnOrdering) return;
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
    setDragOverIndex(null);
    if (dragIndex === dropIndex) return;
    moveColumn(dragIndex, dropIndex);
  };

  const moveColumn = (fromIndex, toIndex) => {
    const newSorted = [...visibleColumns];
    const [movedColumn] = newSorted.splice(fromIndex, 1);
    newSorted.splice(toIndex, 0, movedColumn);

    const updatedPositions = { ...columnVisibility };
    newSorted.forEach((col, index) => {
      updatedPositions[col.key] = {
        ...updatedPositions[col.key],
        position: index,
      };
    });
    setColumnVisibility(updatedPositions);
    if (onVisibilityChange) {
      onVisibilityChange(updatedPositions);
    }
  };

  const headerRender = (
    <tr>
      {!hideIcon && (
        <th
          className="headerFilter"
          ref={popoverButtonRef}
          onClick={
            enableColumnVisibility
              ? (e) => {
                  e.stopPropagation();
                  buttonClickedRef.current = true;
                  if (showPopover) {
                    setShowPopover(false);
                    return;
                  }
                  const rect = popoverButtonRef.current.getBoundingClientRect();
                  setPopoverPosition({ top: rect.bottom, left: rect.left });
                  setShowPopover(true);
                }
              : undefined
          }
        >
          {enableColumnVisibility && (
            <i className="material-icons" style={{ cursor: "pointer" }}>
              more_horizontal
            </i>
          )}
        </th>
      )}

      {visibleColumns.map((col, index) => {
        const isSorted = sortKey && ((col.sort && sortKey === col.sort) || sortKey === col.key);
        let sortIcon = null;
        if (isSorted) {
          sortIcon = (
            <i className="material-icons sort-icon" style={{ fontSize: "16px", verticalAlign: "middle" }}>
              {sortDirection === "asc" ? "arrow_upward" : "arrow_downward"}
            </i>
          );
        }

        return (
          <th
            key={col.key}
            draggable={enableColumnOrdering}
            onDragStart={enableColumnOrdering ? (e) => handleDragStart(e, index) : undefined}
            onDragOver={enableColumnOrdering ? (e) => handleDragOver(e, index) : undefined}
            onDrop={enableColumnOrdering ? (e) => handleDrop(e, index) : undefined}
            onClick={() => setSortKeyFn(col.key, col.sort)}
            className={`
      ${col.sort || col.key ? "sortable" : ""}
      ${isSorted ? "sort " + sortDirection : ""}
      ${col.className || ""}
      ${dragOverIndex === index ? (dragStartIndex > index ? "th-drag-over-left" : "th-drag-over-right") : ""}
    `}
            style={{ cursor: "pointer" }}
          >
            {col.header || capitalize(col.key)} {sortIcon}
          </th>
        );
      })}
    </tr>
  );

  const rowsRender = filteredData.sort(sortFn).map((row, indexRow) => {
    let defaultClasses = config.linkRow || config.onClickRow ? "pointer" : "";
    const extraClasses = typeof rowClassName === "function" ? rowClassName(row, indexRow) : "";
    const combinedClasses = `${defaultClasses} ${extraClasses}`.trim();

    let icon = typeof rowIcon === "function" ? rowIcon(row, indexRow) : rowIcon;

    return (
      <tr
        key={`row_${indexRow}`}
        className={combinedClasses}
        onClick={() => onClickRow(row, row.index, filteredData, indexRow)}
        draggable={!!onRowOrderChange && draggableRows}
        onDragStart={!!onRowOrderChange && draggableRows ? () => setDraggedRowIndex(indexRow) : undefined}
        onDragOver={!!onRowOrderChange && draggableRows ? (e) => e.preventDefault() : undefined}
        onDrop={
          !!onRowOrderChange && draggableRows
            ? () => {
                if (draggedRowIndex === null || draggedRowIndex === indexRow) return;
                const newOrder = [...filteredData];
                const [removed] = newOrder.splice(draggedRowIndex, 1);
                newOrder.splice(indexRow, 0, removed);
                setDraggedRowIndex(null);
                onRowOrderChange(newOrder);
              }
            : undefined
        }
        style={!!onRowOrderChange && draggableRows ? { cursor: "ns-resize" } : undefined}
      >
        {!hideIcon && <td className="table-icon">{icon ? <i className="material-symbols-outlined">{icon}</i> : null}</td>}
        {visibleColumns.map((col, indexCol) => (
          <td key={`row_${indexCol}`} className={col.className || ""}>
            {col.template ? col.template(row, indexRow) : row[col.key]}
          </td>
        ))}
      </tr>
    );
  });

  const onClickRow = (row, indexRow, filteredList, filteredIndex) => {
    if (config.onClickRow) {
      config.onClickRow(row, indexRow, filteredList, filteredIndex);
    } else if (config.linkRow) {
      navigate(config.linkRow(row, indexRow, filteredList, filteredIndex));
    }
  };

  const popover = (
    <div
      className="popover"
      ref={popoverRef}
      style={{
        top: popoverPosition.top,
        left: popoverPosition.left,
      }}
    >
      {config.columns.map((col) => (
        <div key={col.key} className="popover-row">
          <input
            type="checkbox"
            checked={columnVisibility[col.key]?.visibility ?? true}
            onChange={() => toggleVisibility(col.key)}
          />
          {col.header || capitalize(col.key)}
        </div>
      ))}
    </div>
  );

  return (
    <>
      {enableColumnVisibility && showPopover && popover}
      <table className={className}>
        <thead>{headerRender}</thead>
        <tbody>
          {loading ? (
            <tr className="loader-container">
              <td colSpan={visibleColumns.length + (hideIcon ? 0 : 1)}>
                <div className="loader">
                  <img alt="loader" src={loader}></img>
                  <div>Loading ...</div>
                </div>
              </td>
            </tr>
          ) : filteredData.length > 0 ? (
            rowsRender
          ) : (
            <tr className="empty-row">
              <td colSpan={visibleColumns.length + (hideIcon ? 0 : 1)} style={{ textAlign: "left" }}>
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
        {showLoadMore && (
          <tfoot onClick={loadMore}>
            <tr>
              <td colSpan={visibleColumns.length + (hideIcon ? 0 : 1)} style={{ cursor: "pointer" }}>
                Load more...
              </td>
            </tr>
          </tfoot>
        )}
      </table>
      {showFooter && (
        <div className="table-footer-sticky">
          {paginationCountText} {filteredData.length}
        </div>
      )}
    </>
  );
});
